import type { SeatRequestEntry } from "../../seatings/seat-requests";
import type { AttendeeRecord } from "../use-attendee-records";
import { formatRequestTime, type AdminRequestRow } from "./RequestList";

/** Unique within the seat-change type: one request per attendee per seating. */
export const seatChangeRowId = (seatingId: string, attendeeId: string) =>
  `${seatingId}:${attendeeId}`;

const free = (taken: number, capacity: number) =>
  taken >= capacity ? "Full" : `${capacity - taken} of ${capacity} seats free`;

/**
 * Seat change requests (TNG-94) as Requests-tab rows, next to the table
 * switch requests.
 *
 * The current dinner table is read from the published copy as it is now,
 * which an accepted request or a republish may have changed since the
 * attendee asked. A table or seating deleted since reads by the name the
 * request kept.
 */
export function seatChangeRows({
  entries,
  attendees,
  timeZone,
}: {
  entries: SeatRequestEntry[];
  attendees: AttendeeRecord[];
  timeZone?: string;
}): AdminRequestRow[] {
  const attendeesById = new Map(attendees.map((a) => [a.id, a]));
  return entries.map(({ request, seating, tables }): AdminRequestRow => {
    const attendee = attendeesById.get(request.attendeeId);
    const open = request.status === "OPEN";
    const current =
      tables.find((table) => table.attendeeIds.includes(request.attendeeId)) ??
      tables.find((table) => table.id === request.fromTableId);
    const target = tables.find((table) => table.id === request.toTableId);
    const seatingName = request.seatingName ?? seating.name;
    return {
      type: "seat-change",
      id: seatChangeRowId(seating.id, request.attendeeId),
      open,
      person: attendee
        ? {
            attendeeId: attendee.id,
            name: attendee.name,
            role: attendee.role,
            company: attendee.company,
            avatar: attendee.avatar,
          }
        : null,
      personFallback: "Former attendee",
      current: {
        caption: `Dinner table, ${seatingName}`,
        label: current?.name ?? "Not seated",
      },
      requested: {
        caption: "Asks for",
        label:
          target?.name ??
          (request.toTableName
            ? `${request.toTableName} (removed)`
            : "A removed dinner table"),
        detail:
          open && target
            ? free(
                target.attendeeIds.filter((id) => id !== request.attendeeId)
                  .length,
                target.capacity,
              )
            : undefined,
      },
      reason: request.reason,
      requestedAt: request.requestedAt?.toDate() ?? null,
      decidedAt: request.decidedAt?.toDate() ?? null,
      ...(open ? {} : outcome(request, timeZone)),
    };
  });
}

function outcome(
  request: SeatRequestEntry["request"],
  timeZone?: string,
): Pick<AdminRequestRow, "outcome" | "outcomeTone"> {
  const when = request.decidedAt
    ? ` ${formatRequestTime(request.decidedAt.toDate(), timeZone)}`
    : "";
  switch (request.status) {
    case "ACCEPTED":
      return { outcome: `Accepted${when}`, outcomeTone: "positive" };
    case "DECLINED":
      return { outcome: `Declined${when}`, outcomeTone: "negative" };
    default:
      return {
        outcome:
          request.cancelReason === "TABLE_DELETED"
            ? `Cancelled${when}: the dinner table was removed`
            : request.cancelReason === "SEAT_CHANGED"
              ? `Cancelled${when}: their seat changed in a publish`
              : `Cancelled${when}: the seating was taken down`,
        outcomeTone: "neutral",
      };
  }
}
